import { ActivateDeviceRequest, Device } from '@/api';
import { useCallback, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getFallbackErrorKey, isApiError } from '@/common/util';
import { useDevicesTranslation } from '../hooks';
import { addOrUpdateAllDevicesCache } from '../queries/devices-cache';

/** Everything the caller chooses per activation. */
export type ActivateDeviceVariables = {
    /** The device id read off the QR code. */
    deviceId: string;
    data: ActivateDeviceRequest;
};

type UseActivateDeviceActionData = {
    activateDevice: (variables: ActivateDeviceVariables) => Promise<Device>;
    loading: boolean;
    error: string;
};

/**
 * Claiming a scanned device for a company. The first step of the activation
 * wizard; naming it and turning its alerts on is
 * `useConfigureActivatedDeviceAction`.
 *
 * Raises no toast, unlike every other action here: the wizard replaces its whole
 * body with an error screen built from `error`, and a toast on top of that would
 * say the same thing twice.
 *
 * No `isAllowed`/`reason`: the button that opens the wizard is gated by the
 * devices page's own `canEditDevices`, and the licence and subscription
 * refusals are the server's to make.
 */
export function useActivateDeviceAction(): UseActivateDeviceActionData {
    const { t } = useDevicesTranslation();
    // The client is swapped out on token refresh, so never capture it: read
    // `openApiRef.current` at call time or you keep using a dead token.
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const queryClient = useQueryClient();

    // One place that turns a failure into a message. Every error
    // `POST /v3/devices/{deviceId}/activate` declares gets a branch.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            if (isApiError(error, 'DEVICE_ALREADY_ACTIVATED')) {
                return t('shared.errors.device_already_activated');
            }
            if (isApiError(error, 'SIM_CLOSED')) {
                return t('shared.errors.sim_closed');
            }
            if (isApiError(error, 'DEVICE_NOT_FOUND')) {
                return t('shared.errors.device_not_found');
            }
            if (isApiError(error, 'DEVICE_NOT_SETUP')) {
                return t('shared.errors.device_not_setup');
            }
            if (isApiError(error, 'NOT_SUBSCRIBED')) {
                return t('shared.errors.not_subscribed');
            }
            if (isApiError(error, 'NO_MORE_LICENSES')) {
                return t('shared.errors.no_more_licenses');
            }
            if (isApiError(error, 'NO_PERMISSION')) {
                return t('shared.errors.unauthorised');
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                // The server names the offending field; it is more use than
                // anything generic we could write.
                return error.response?.data.message.join(', ') ?? '';
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        mutationFn: (variables: ActivateDeviceVariables) =>
            openApiRef.current.devices
                .activateDevice(variables.deviceId, variables.data)
                .then((response) => response.data.data.device),
        onSuccess: (device) => {
            addOrUpdateAllDevicesCache(queryClient, device);
        }
        // No `onError`: see the note on the toast above.
    });

    return {
        activateDevice: (variables) => mutation.mutateAsync(variables),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : ''
    };
}
